"use client";

import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Cog, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type Lang = "vi" | "en";
interface TextContent {
  tagTracks: string;
  title: string;
  subtitle: string;
  body: string;
  disciplinesHeader: string;
  bookTrial: string;
  viewCurriculum: string;
  labOverlayTitle: string;
  labOverlayDesc: string;
  labActiveBadge: string;
  statTracksNum: string;
  statTracksDesc: string;
  statStemTitle: string;
  statStemDesc: string;
  disciplines: {
    id: string;
    name: string;
    badge: string;
  }[];
}
const contentData: Record<Lang, TextContent> = {
  vi: {
    tagTracks: "Chương trình đào tạo",
    title: "LEGO Robotics — Học công nghệ qua lắp ráp, lập trình và sáng tạo",
    subtitle: "Chương trình đào tạo LEGO Robotics kết hợp tư duy cơ học, cấu trúc lắp ráp thực tế và lập trình điều khiển trực quan cho học viên.",
    body: "Tại ROBOTO, học viên khám phá khoa học công nghệ thông qua các bộ kit LEGO robotics chuyên dụng: trực tiếp lắp ráp khung cơ khí, tích hợp cảm biến, động cơ và thực hành lập trình vận hành mô hình. Để xem chi tiết các cấp độ phát triển toàn diện theo từng giai đoạn, phụ huynh có thể tham khảo Lộ trình Robotics được thiết kế bài bản tại trung tâm.",
    disciplinesHeader: "Trọng tâm chương trình & hoạt động:",
    bookTrial: "Khám phá lộ trình Robotics",
    viewCurriculum: "Liên hệ tư vấn",
    labOverlayTitle: "Robotics R&D Lab",
    labOverlayDesc: "Thực hành lập trình & kiểm thử vi điều khiển",
    labActiveBadge: "Lab Hoạt Động",
    statTracksNum: "3 Giai đoạn",
    statTracksDesc: "Từ nền tảng đến chuyên sâu",
    statStemTitle: "Học cụ LEGO & WhalesBot",
    statStemDesc: "Thiết bị chính khóa",
    disciplines: [{
      id: "lego-robotics",
      name: "LEGO Robotics",
      badge: "Chương trình chính"
    }, {
      id: "competitions",
      name: "Robotics Thi đấu",
      badge: "Đấu trường & Giải đấu"
    }]
  },
  en: {
    tagTracks: "Core Curriculum",
    title: "LEGO Robotics — Learn Technology Through Building, Coding & Innovation",
    subtitle: "A hands-on LEGO Robotics program integrating mechanical engineering principles, structural builds, and intuitive code execution.",
    body: "At ROBOTO, students dive into practical technology using specialized LEGO robotics platforms: assembling kinetic mechanisms, configuring sensors and motors, and programming automated behaviors. To discover the complete stage-by-stage learning progression, explore our dedicated Robotics Learning Pathway.",
    disciplinesHeader: "Key focus & activities:",
    bookTrial: "Explore the Robotics Pathway",
    viewCurriculum: "Request consultation",
    labOverlayTitle: "Robotics R&D Lab",
    labOverlayDesc: "Hands-on micro-controller debugging",
    labActiveBadge: "Active Lab",
    statTracksNum: "3 Stages",
    statTracksDesc: "Foundations to elite",
    statStemTitle: "LEGO & WhalesBot Kits",
    statStemDesc: "Core program equipment",
    disciplines: [{
      id: "lego-robotics",
      name: "LEGO Robotics",
      badge: "Core Program"
    }, {
      id: "competitions",
      name: "Competitions & Arenas",
      badge: "Tournaments"
    }]
  }
};
const disciplineIcons = [{
  id: "lego-robotics",
  icon: Cog
}, {
  id: "competitions",
  icon: Trophy
}];
const disciplineTargets: Record<string, string> = {
  "lego-robotics": "#program-introduction",
  competitions: "/ProjectsCompetitionsPage#competitions-we-prepare-for"
};
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO mascot"
};
function getStoredLang(): Lang {
  if (typeof window === "undefined") return "vi";
  try {
    const val = localStorage.getItem("roboto_lang");
    if (val === "vi" || val === "en") return val;
  } catch {
    // ignore local storage errors
  }
  return "vi";
}
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<Lang>("vi");
  useEffect(() => {
    setLang(getStoredLang());
    const handleCustomEvent = (e: Event) => {
      const custom = e as CustomEvent<{
        lang?: Lang;
      }>;
      if (custom.detail?.lang === "vi" || custom.detail?.lang === "en") {
        setLang(custom.detail.lang);
      }
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomEvent);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);
  const t = contentData[lang] || contentData.vi;
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="programs-overview" data-nav="light" className="bg-background text-foreground pt-16 pb-28 sm:pb-32 lg:py-24 relative overflow-hidden">
      {/* Decorative Lab Blueprint Rail */}
      <div className="absolute top-0 inset-x-0 h-px bg-border flex items-center max-w-[1440px] mx-auto px-6 lg:px-16 xl:px-24">
        <span className="w-1.5 h-1.5 bg-secondary -mt-px" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-16 xl:px-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column: Heading and Program Details */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 flex flex-col gap-6">
            {/* Stage Spec Tag */}
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/10 px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider text-secondary">
                {t.tagTracks}
              </span>
            </div>

            {/* Primary Page Title */}
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              {t.title}
            </h1>

            {/* Supporting line */}
            <p className="font-sans text-lg sm:text-xl font-medium text-foreground/90 leading-relaxed">
              {t.subtitle}
            </p>

            {/* Body paragraph */}
            <p className="font-sans text-base text-muted-foreground leading-relaxed max-w-2xl">
              {t.body}
            </p>

            {/* Mini discipline tags */}
            <div className="pt-2">
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground block mb-3">
                {t.disciplinesHeader}
              </span>
              <div className="flex flex-wrap gap-2.5">
                {t.disciplines.map((item, i) => {
                const iconConfig = disciplineIcons.find(d => d.id === item.id) || disciplineIcons[i];
                const Icon = iconConfig.icon;
                const targetHref = disciplineTargets[item.id] || `#track-${item.id}`;
                return <Link key={item.id} data-index={i} to={targetHref} className="group inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 shadow-2xs cursor-pointer transition-[border-color,box-shadow,transform] duration-200 hover:border-secondary hover:shadow-xs hover:scale-[1.03] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/70 focus-visible:border-secondary">
                      <div className="p-1 rounded-md bg-secondary/15 text-secondary shrink-0 flex items-center justify-center transition-colors duration-200 group-hover:bg-secondary group-hover:text-secondary-foreground">
                        <Icon className="size-3.5" />
                      </div>
                      <span className="font-sans text-xs font-semibold text-card-foreground transition-colors duration-200 group-hover:text-primary">
                        {item.name}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded transition-colors duration-200 group-hover:bg-secondary/10 group-hover:text-secondary-foreground">
                        {item.badge}
                      </span>
                    </Link>;
              })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 px-7 py-3.5 h-auto text-sm font-semibold rounded-lg shadow-sm">
                <Link to="/RoboticsLearningPathwayPage">{t.bookTrial}</Link>
              </Button>

              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3.5 h-auto text-sm font-semibold rounded-lg">
                <Link to="/FreeTrialContactPage" className="inline-flex items-center gap-2">
                  <span>{t.viewCurriculum}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Classroom Image & Technical Callout */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-2xl border border-border bg-card p-3 shadow-md overflow-hidden">
              <div className="relative overflow-hidden rounded-xl bg-muted aspect-[4/3]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/young_students_collaborating_robotics_hardware_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/young_students_collaborating_robotics_hardware_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/young_students_collaborating_robotics_hardware_lab.png-wvc-sizes" alt="Young students collaborating on robotics hardware in a lab classroom" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />

                {/* Visual Label overlay */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-primary-foreground bg-primary/70 backdrop-blur-xs p-3 rounded-lg border border-border/20">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-wider text-secondary">
                      {t.labOverlayTitle}
                    </p>
                    <p className="font-sans text-xs font-medium text-primary-foreground/90">
                      {t.labOverlayDesc}
                    </p>
                  </div>
                  <span className="font-mono text-xs px-2 py-0.5 rounded bg-secondary/20 text-secondary border border-secondary/40">
                    {t.labActiveBadge}
                  </span>
                </div>
              </div>

              {/* Lab feature highlight badge below image */}
              <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                <div className="bg-muted/60 p-2.5 rounded-lg border border-border/60">
                  <p className="font-serif text-lg font-bold text-foreground">
                    {t.statTracksNum}
                  </p>
                  <p className="font-sans text-xs text-muted-foreground">
                    {t.statTracksDesc}
                  </p>
                </div>
                <div className="bg-muted/60 p-2.5 rounded-lg border border-border/60">
                  <p className="font-serif text-lg font-bold text-foreground">
                    {t.statStemTitle}
                  </p>
                  <p className="font-sans text-xs text-muted-foreground">
                    {t.statStemDesc}
                  </p>
                </div>
              </div>
            </div>

            {/* Circuit-Cyan blueprint accent dot */}
            <div className="absolute -top-3 -right-3 size-6 border-t-2 border-r-2 border-secondary hidden sm:block" aria-hidden="true" />
          </motion.div>
        </div>
      </div>

      {/* Static Floating Decorative Mascot */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: shouldReduceMotion ? 0.01 : 0.4,
      ease: "easeOut"
    }} className="absolute bottom-3 right-4 sm:bottom-4 sm:right-6 lg:bottom-6 lg:right-12 xl:right-16 pointer-events-none select-none z-0" aria-hidden="true">
        <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt={defaultMascot.alt} loading="lazy" decoding="async" className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 xl:w-36 xl:h-36 object-contain bg-transparent pointer-events-none select-none drop-shadow-md" />
      </motion.div>
    </section>;
}